import { useEffect, useState } from "react";
import { Gift, Users, Sparkles, CalendarHeart, Shuffle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const TOUR_VERSION = "v1";
const storageKey = (userId: number | string) =>
  `bfs_onboarding_done_${TOUR_VERSION}_${userId}`;

export const ONBOARDING_TOUR_EVENT = "bfs:start-tour";

const steps = [
  {
    icon: Gift,
    title: "Build your wishlist",
    description:
      "Add what you'd love to receive — paste a product link or add it manually. Organise items into sections and keep everything in one place.",
  },
  {
    icon: Users,
    title: "Share with friends",
    description:
      "Connect with friends and share your wishlists. Friends can reserve a gift so two people never buy the same thing.",
  },
  {
    icon: Sparkles,
    title: "AI Gift Assistant",
    description:
      "Not sure what to give? The AI Gift Assistant suggests thoughtful gift ideas based on interests and occasions.",
  },
  {
    icon: CalendarHeart,
    title: "Events & reminders",
    description:
      "Plan events and never miss a birthday or holiday — set reminders so you always have time to find the perfect gift.",
  },
  {
    icon: Shuffle,
    title: "Randomizer & games",
    description:
      "Run a Secret Santa gift exchange with the Randomizer, and play party games together. You're all set — enjoy BuyForS!",
  },
];

export const OnboardingTour = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    try {
      const done = localStorage.getItem(storageKey(user.id));
      if (!done) {
        setStepIndex(0);
        setOpen(true);
      }
    } catch {
      // localStorage unavailable; skip the tour silently
    }
  }, [user?.id]);

  useEffect(() => {
    const handleStart = () => {
      setStepIndex(0);
      setOpen(true);
    };
    window.addEventListener(ONBOARDING_TOUR_EVENT, handleStart);
    return () => window.removeEventListener(ONBOARDING_TOUR_EVENT, handleStart);
  }, []);

  const finish = () => {
    if (user?.id) {
      try {
        localStorage.setItem(storageKey(user.id), "1");
      } catch {
        // ignore storage errors
      }
    }
    setOpen(false);
  };

  if (!user?.id) return null;

  const step = steps[stepIndex];
  const Icon = step.icon;
  const isLast = stepIndex === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : finish())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Icon className="h-7 w-7 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">{step.title}</DialogTitle>
          <DialogDescription className="text-center">
            {step.description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center gap-2 py-2">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === stepIndex ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30"
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button variant="ghost" onClick={finish}>
            Skip
          </Button>
          <div className="flex gap-2">
            {stepIndex > 0 && (
              <Button
                variant="outline"
                onClick={() => setStepIndex((i) => i - 1)}
              >
                Back
              </Button>
            )}
            <Button
              onClick={() =>
                isLast ? finish() : setStepIndex((i) => i + 1)
              }
            >
              {isLast ? "Get started" : "Next"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
